import { useEffect, useState, useRef } from "react";
import ReactSelect from "react-select";
import Swal from "sweetalert2";

// componentes
import EstadoBadge from "../components/EstadoBadge";
import Loader from "../components/Loader";

// conexiones a la API
import { listIngresosLazy, AddIngresos } from "../api/ingresos";
import { listTipoDocumento002, listDepartamento012, listMunicipio013, listActividadEconomica19 } from "../api/hacienda"
import { listcocinaInsumoLazy } from "../api/ventaCocinaInsumo";
import { listProveedor, createProveedor } from "../api/proveedores";

// utilidades
import { DataIngreso } from '../utils/validaciones';
import { formatDateReadable, formatDate } from '../utils/fechas';

const today = formatDate(new Date());

const IngresosInsumos = () => {
  const [loading, setLoading] = useState(true);
  const [ingresos, setIngresos] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [buscar, setBuscar] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [editMode, setEditMode] = useState(false);

  // Modal principal
  const [showModal, setShowModal] = useState(false);
  const [proveedores, setProveedores] = useState([]);
  const [proveedorSeleccionado, setProveedorSeleccionado] = useState(null);
  const [insumosSeleccionados, setInsumosSeleccionados] = useState([]);
  const [fechaIngreso, setFechaIngreso] = useState(today);
  const [numeroFactura, setNumeroFactura] = useState("");
  const [codigoFactura, setCodigoFactura] = useState("");
  const [tipoFactura, setTipoFactura] = useState("");
  const [iva, setIva] = useState(0);

  // Modal de insumos
  const [showInsumoModal, setShowInsumoModal] = useState(false);
  const [insumos, setInsumos] = useState([]);
  const [buscarInsumo, setBuscarInsumo] = useState("");
  const [pageIns, setPageIns] = useState(1);
  const [totalPagesIns, setTotalPagesIns] = useState(1);
  const [limitIns] = useState(10);

  const [tipoDocumento, setTipoDocumento] = useState([]);
  const fileInputRef = useRef(null);

  // Estados para Modal de Proveedor (Importación JSON)
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [departamentos, setDepartamentos] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [actividad, setActividadEconomica] = useState([]);
  const [pendingJsonImport, setPendingJsonImport] = useState(null);
  const [rawJsonString, setRawJsonString] = useState(null);
  const [formProveedor, setFormProveedor] = useState({
    name: "",
    nombre_comercial: "",
    nit: "",
    dui: "00000000-0",
    nrc: "",
    actividad: "",
    departamento: "",
    municipio: "",
    direccion: "",
    telefono: "",
    correo: "",
    status: "1",
  });

  const [form, setForm] = useState({});
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedIngreso, setSelectedIngreso] = useState(null);

  const resetForm = () => {
    setProveedorSeleccionado(null);
    setInsumosSeleccionados([]);
    setFechaIngreso(today);
    setNumeroFactura("");
    setCodigoFactura("");
    setTipoFactura("");
    setIva(0);
    setForm({});
    setPendingJsonImport(null);
    setRawJsonString(null);
    setEditMode(false);
    setSelectedIngreso(null);
  };

  const fetchData = async (p = 1) => {
    try {
      setEditMode(false);
      setLoading(true);
      const data = await listIngresosLazy(2, p, limit, buscar);
      setIngresos(data.data);
      setTotalPages(data.totalPages);
      setPage(data.currentPage);

      const doc = await listTipoDocumento002();
      setTipoDocumento(doc.data);

    } catch (error) {
      console.error("Error al obtener los ingresos", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProveedores = async () => {
    try {
      const data = await listProveedor();
      const provOpts = data.data.map(p => ({ value: p.id, label: p.name, nit: p.nit, nrc: p.nrc }));
      setProveedores(provOpts);
      return provOpts;
    } catch (error) {
      console.error("Error al obtener proveedores", error);
      return [];
    }
  };

  const fetchProviderData = async () => {
    try {
      if (departamentos.length === 0) {
        const [dep, mun, act] = await Promise.all([
          listDepartamento012({}),
          listMunicipio013({}),
          listActividadEconomica19({})
        ]);
        setDepartamentos(dep.data || []);
        setMunicipios(mun.data || []);
        setActividadEconomica(act.data || []);
        return { depData: dep.data || [], munData: mun.data || [], actData: act.data || [] };
      }
      return { depData: departamentos, munData: municipios, actData: actividad };
    } catch (error) {
      console.error("Error fetching provider data", error);
      return { depData: [], munData: [], actData: [] };
    }
  };

  const fetchInsumosList = async (p = 1) => {
    try {
      const data = await listcocinaInsumoLazy(p, limitIns, buscarInsumo);
      setInsumos(data.data);
      setTotalPagesIns(data.totalPages);
      setPageIns(data.currentPage);
    } catch (error) {
      console.error("Error al obtener insumos", error);
    }
  };

  useEffect(() => {
    fetchData(page);
    // eslint-disable-next-line 
  }, [page, limit, buscar]);

  useEffect(() => {
    if (showInsumoModal) {
      fetchInsumosList(pageIns);
    }
    // eslint-disable-next-line 
  }, [showInsumoModal, pageIns, buscarInsumo]);

  const handleAddInsumo = (item) => {
    const newIns = {
      value: item.id,
      label: `${item.sku} | ${item.name}`,
      cantidad: 1,
      precio: item.price || 0,
      unidad: item.UnidadMedida?.name || "---",
    };
    const exists = insumosSeleccionados.find(p => p.value === newIns.value);
    if (!exists) {
      setInsumosSeleccionados([...insumosSeleccionados, newIns]);
    }
  };

  const handleChangeCantidad = (index, cantidad) => {
    const updated = [...insumosSeleccionados];
    updated[index].cantidad = cantidad;
    setInsumosSeleccionados(updated);
  };

  const handleChangePrecio = (index, precio) => {
    const updated = [...insumosSeleccionados];
    updated[index].precio = precio;
    setInsumosSeleccionados(updated);
  };

  const handleSelectChange = (value, name) => {
    if (value) {
      setTipoFactura(value.value);
      setForm((prev) => ({ ...prev, [name]: value.value }));
    } else {
      setTipoFactura(null);
      setForm((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleFileImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    resetForm();

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const jsonContent = event.target.result;
        const json = JSON.parse(jsonContent);
        setRawJsonString(jsonContent);

        const { fecEmi, numeroControl, codigoGeneracion, tipoDte } = json.identificacion || {};
        const nitEmisor = json.emisor?.nit;

        setFechaIngreso(fecEmi || "");
        setNumeroFactura(numeroControl || "");
        setCodigoFactura(codigoGeneracion || "");

        const tipoDteStr = String(tipoDte).padStart(2, '0');
        if (!["01", "03", "88"].includes(tipoDteStr)) {
          Swal.fire({
            icon: 'error',
            title: 'Tipo de Documento no permitido',
            text: `El JSON cargado es de tipo ${tipoDteStr}.`,
          });
          e.target.value = null;
          return;
        }

        if (tipoDte && tipoDocumento && tipoDocumento.length > 0) {
          const matchedDoc = tipoDocumento.find(c => {
            const doc = c.typeFact || c;
            return doc.codigo === String(tipoDte).padStart(2, '0');
          });
          if (matchedDoc) {
            const docInfo = matchedDoc.typeFact || matchedDoc;
            setTipoFactura(docInfo.id.toString());
            setForm((prev) => ({ ...prev, tipoDocumento: docInfo.id.toString() }));
          }
        }

        let totalIva = 0;
        if (tipoDteStr === "03" && json.resumen?.tributos) {
          const tributoIva = json.resumen.tributos.find(t => t.codigo === "20");
          if (tributoIva) {
            totalIva = Number(tributoIva.valor) || 0;
          }
        }
        setIva(totalIva);

        const provOpts = await fetchProveedores();
        const nitEmisorStripped = nitEmisor?.replace(/-/g, "");
        const nrcEmisorStr = json.emisor?.nrc;

        let matchedProvider = provOpts.find(p => {
          const matchNit = nitEmisor && (p.nit === nitEmisor || p.nit === nitEmisorStripped);
          const matchNrc = nrcEmisorStr && p.nrc === nrcEmisorStr;
          return matchNit || matchNrc;
        });

        if (matchedProvider) {
          setProveedorSeleccionado({ value: matchedProvider.value, label: matchedProvider.label });
        } else {
          const { depData, munData, actData } = await fetchProviderData();
          const codDep = json.emisor?.direccion?.departamento;
          const codMun = json.emisor?.direccion?.municipio;
          const codAct = json.emisor?.codActividad;

          let idDep = ""; let idMun = ""; let idAct = "";
          const matchedDep = depData.find(d => d.codigo === codDep); if (matchedDep) idDep = matchedDep.id.toString();
          const matchedMun = munData.find(m => m.codigo === codMun); if (matchedMun) idMun = matchedMun.id.toString();
          const matchedAct = actData.find(a => a.codigo === codAct); if (matchedAct) idAct = matchedAct.id.toString();

          setFormProveedor(prev => ({
            ...prev,
            name: json.emisor?.nombre || "",
            nombre_comercial: json.emisor?.nombreComercial || json.emisor?.nombre || "",
            nit: nitEmisor || "",
            nrc: nrcEmisorStr || "",
            telefono: json.emisor?.telefono || "",
            correo: json.emisor?.correo || "",
            direccion: json.emisor?.direccion?.complemento || "",
            departamento: idDep,
            municipio: idMun,
            actividad: idAct
          }));

          setPendingJsonImport({ json, rawItems: json.cuerpoDocumento || [] });
          setShowProviderModal(true);
          e.target.value = null;
          return;
        }

        await continueJsonProcessing(json, json.cuerpoDocumento || []);
        e.target.value = null;

      } catch (error) {
        console.error("Error parsing JSON", error);
      }
    };
    reader.readAsText(file);
  };

  const continueJsonProcessing = async (json, rawItems) => {
    const mappedItems = [];
    for (const item of rawItems) {
      mappedItems.push({
        value: null,
        label: `⚠️ VINCULAR: ${item.codigo || "Sin código"} | ${item.descripcion}`,
        cantidad: item.cantidad || 1,
        precio: item.precioUni || 0,
        isPlaceholder: true,
        jsonInfo: { codigo: item.codigo || "SIN CÓDIGO", descripcion: item.descripcion }
      });
    }
    setInsumosSeleccionados(mappedItems);
    setShowModal(true);
  };

  const handleProveedorSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = { ...formProveedor, id_actividad_economica: formProveedor.actividad, id_departamento: formProveedor.departamento, id_municipio: formProveedor.municipio };
      const res = await createProveedor(data);
      const newProvOpts = await fetchProveedores();
      if (res?.id) {
        const newlyCreated = newProvOpts.find(p => p.value === res.id);
        if (newlyCreated) setProveedorSeleccionado({ value: newlyCreated.value, label: newlyCreated.label });
      }
      setShowProviderModal(false);
      if (pendingJsonImport) {
        await continueJsonProcessing(pendingJsonImport.json, pendingJsonImport.rawItems);
        setPendingJsonImport(null);
      }
    } catch (error) {
      console.error("Error al registrar proveedor", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateImportedInsumo = (index, selectedItem) => {
    if (!selectedItem) return;
    const updated = [...insumosSeleccionados];
    updated[index] = {
      ...updated[index],
      value: selectedItem.id,
      label: `${selectedItem.sku} | ${selectedItem.name}`,
      isPlaceholder: false
    };
    setInsumosSeleccionados(updated);
  };

  const opcionesTipoDocumento = (tipoDocumento || [])
    .filter((c) => ["01", "03", "88"].includes((c.typeFact || c).codigo))
    .map((c) => {
      const doc = c.typeFact || c;
      return { value: doc.id.toString(), label: `${doc.codigo} | ${doc.name}` };
    });

  const handleProcesarIngreso = async () => {
    setSubmitting(true);
    try {
      const isJsonImport = !!rawJsonString;
      const detalleFinal = insumosSeleccionados.map((p) => ({
        id_producto: p.value,
        cantidad: Number(p.cantidad) || 0,
        precio: isJsonImport ? (Number(p.precio) * 1.13) : Number(p.precio)
      }));

      let totalIngreso = 0;
      if (isJsonImport) {
        try {
          const parsed = JSON.parse(rawJsonString);
          totalIngreso = Number(parsed?.resumen?.totalPagar) || 0;
        } catch (e) {
          totalIngreso = detalleFinal.reduce((acc, curr) => acc + (curr.cantidad * curr.precio), 0);
        }
      } else {
        totalIngreso = detalleFinal.reduce((acc, curr) => acc + (curr.cantidad * curr.precio), 0);
      }

      const ingresoData = {
        codigo_factura: codigoFactura,
        numero_factura: numeroFactura,
        id_proveedor: proveedorSeleccionado?.value || null,
        fecha_ingreso: fechaIngreso,
        id_sucursal: 3,
        id_typeFacts: tipoFactura,
        ingresoDetalle: detalleFinal,
        total: totalIngreso,
        iva: iva,
        json: rawJsonString,
        tipo: 2
      };

      if (DataIngreso(ingresoData)) {
        await AddIngresos(ingresoData);
        setShowModal(false);
        resetForm();
        await fetchData(1);
      }
    } catch (error) {
      console.error("Error al procesar ingreso", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-3 sm:p-6 max-w-7xl mx-auto">
      {/* Encabezado */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-800">Gestión de Ingresos (Insumos)</h2>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">Registre entradas de insumos y materia prima</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input type="file" ref={fileInputRef} onChange={handleFileImport} accept=".json,application/json" className="hidden" />
          <button onClick={() => fileInputRef.current?.click()} className="bg-white border border-slate-300 text-slate-700 px-4 py-2.5 rounded-lg hover:bg-slate-50 transition-colors shadow-sm font-medium flex items-center justify-center gap-2">
             Importar JSON
          </button>
          <button onClick={() => { resetForm(); fetchProveedores(); setShowModal(true); }} className="bg-indigo-600 text-white px-4 py-2.5 rounded-lg hover:bg-indigo-700 transition-colors shadow-sm font-medium flex items-center justify-center gap-2">
            Nuevo Ingreso
          </button>
        </div>
      </div>

      {/* Filtros y Paginación */}
      <div className="flex flex-col sm:flex-row items-center gap-4 mb-6 bg-white p-4 rounded-xl shadow-sm border border-slate-200">
        <div className="relative flex-grow">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-slate-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Buscar ingreso por factura, proveedor..."
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-slate-50 focus:bg-white text-sm"
          />
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500 font-medium whitespace-nowrap">Mostrar:</span>
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
            className="border border-slate-300 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-sm"
          >
            {[10, 15, 25, 50, 100, 250, 500].map(num => (
              <option key={num} value={num}>
                {num} registros
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden relative">
        {loading && <div className="absolute inset-0 bg-white/70 flex justify-center items-center z-10"><Loader /></div>}
        {/* Tabla — solo md+ */}
        <div className="hidden md:block overflow-x-auto custom-scrollbar">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-slate-500 bg-slate-50 uppercase border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 font-semibold">Fecha</th>
                <th className="px-4 py-3 font-semibold">Proveedor</th>
                <th className="px-4 py-3 font-semibold">Tipo de factura</th>
                <th className="px-4 py-3 font-semibold text-right">Total Ingreso</th>
                <th className="px-4 py-3 font-semibold">Sucursal</th>
                <th className="px-4 py-3 font-semibold">Codigo Generacion</th>
                <th className="px-4 py-3 font-semibold">Numero Control</th>
                <th className="px-4 py-3 font-semibold text-center">Estado</th>
                <th className="px-4 py-3 font-semibold text-center sticky right-0 bg-slate-100 border-l border-slate-200 z-10 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.05)]">Opciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {ingresos.map((i) => (
                <tr key={i.id} className="hover:bg-indigo-50/50 transition-colors group">
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{formatDateReadable(i.fecha_ingreso)}</td>
                  <td className="px-4 py-3 font-medium text-slate-700 whitespace-nowrap">{i.Proveedore?.name}</td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                    {i.TipoDocumento?.codigo} <br /> {i.TipoDocumento?.name}
                  </td>
                  <td className="px-4 py-3 text-right text-emerald-600 font-bold tracking-tight">${Number(i.total || 0).toFixed(2)}</td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                    {i.Sucursale?.codigo_sucursal} <br /> {i.Sucursale?.nombre}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500 truncate max-w-[120px]">{i.codigo_factura || "---"}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{i.numero_factura || "---"}</td>
                  <td className="px-4 py-3 text-center">
                    <EstadoBadge status={i.status} />
                  </td>
                  <td className="px-4 py-3 sticky right-0 bg-slate-100 group-hover:bg-slate-200 border-l border-slate-200 z-0 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.03)] transition-colors text-center">
                    <button
                      onClick={() => { setSelectedIngreso(i); setShowDetailModal(true); }}
                      className="bg-white border border-slate-200 text-indigo-600 px-3 py-1.5 rounded-lg hover:bg-indigo-50 hover:border-indigo-200 transition-all text-xs font-bold flex items-center gap-2 mx-auto"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      Ver Detalle
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Cards — solo móvil */}
        <div className="md:hidden divide-y divide-slate-100">
          {ingresos.map((i) => (
            <div key={i.id} className="p-4 bg-white">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-slate-800 text-sm truncate">{i.Proveedore?.name || "Sin proveedor"}</p>
                  <p className="text-[11px] text-slate-500 font-medium">{formatDateReadable(i.fecha_ingreso)}</p>
                </div>
                <EstadoBadge status={i.status} />
              </div>

              <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-[11px] mb-4">
                <div>
                  <span className="text-slate-400 font-black uppercase tracking-widest text-[9px]">Total</span>
                  <p className="text-emerald-600 font-black text-sm">${Number(i.total || 0).toFixed(2)}</p>
                </div>
                <div>
                  <span className="text-slate-400 font-black uppercase tracking-widest text-[9px]">Documento</span>
                  <p className="text-slate-700 font-bold">{i.TipoDocumento?.codigo || "---"}</p>
                </div>
                <div className="col-span-2 grid grid-cols-2 gap-2 bg-slate-50 p-2 rounded-lg border border-slate-100">
                  <div>
                    <span className="text-slate-400 font-black uppercase tracking-widest text-[9px]">No. Control</span>
                    <p className="text-slate-600 font-mono text-[10px] truncate">{i.numero_factura || "---"}</p>
                  </div>
                  <div>
                    <span className="text-slate-400 font-black uppercase tracking-widest text-[9px]">Cód. Gen.</span>
                    <p className="text-slate-600 font-mono text-[10px] truncate">{i.codigo_factura || "---"}</p>
                  </div>
                </div>
              </div>

              <button
                onClick={() => {
                  setSelectedIngreso(i);
                  setShowDetailModal(true);
                }}
                className="w-full bg-indigo-600 text-white py-2.5 rounded-xl hover:bg-indigo-700 transition-all text-xs font-black uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg shadow-indigo-100"
              >
                Ver Detalle
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Paginación — Estilo Identico a Productos.jsx */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mt-4 sm:mt-6 px-1 gap-3">
        <p className="text-sm text-slate-500 text-center sm:text-left">
          Página <span className="font-medium">{page}</span> de <span className="font-medium">{totalPages}</span>
        </p>
        <div className="flex gap-1 bg-white p-1 rounded-lg border border-slate-200 justify-center overflow-x-auto">
          <button
            disabled={page === 1}
            onClick={() => fetchData(page - 1)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${page === 1
              ? "text-slate-400 cursor-not-allowed"
              : "text-slate-600 hover:bg-slate-100"
              }`}
          >
            ← Ant
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => {
            if (totalPages > 7) {
              if (num === 1 || num === totalPages || (num >= page - 1 && num <= page + 1)) {
                return (
                  <button
                    key={num}
                    onClick={() => fetchData(num)}
                    className={`min-w-[32px] px-2 py-1.5 rounded-md text-sm font-medium transition-colors ${page === num
                      ? "bg-indigo-50 text-indigo-600"
                      : "text-slate-600 hover:bg-slate-100"
                      }`}
                  >
                    {num}
                  </button>
                );
              } else if (num === page - 2 || num === page + 2) {
                return <span key={num} className="px-2 py-1.5 text-slate-400">...</span>;
              }
              return null;
            }

            return (
              <button
                key={num}
                onClick={() => fetchData(num)}
                className={`min-w-[32px] px-2 py-1.5 rounded-md text-sm font-medium transition-colors ${page === num
                  ? "bg-indigo-50 text-indigo-600"
                  : "text-slate-600 hover:bg-slate-100"
                  }`}
              >
                {num}
              </button>
            )
          })}

          <button
            disabled={page === totalPages}
            onClick={() => fetchData(page + 1)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${page === totalPages
              ? "text-slate-400 cursor-not-allowed"
              : "text-slate-600 hover:bg-slate-100"
              }`}
          >
            Sig →
          </button>
        </div>
      </div>

      {/* Modal Principal (IDÉNTICO A INGRESOS.JSX) */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex justify-center items-center z-[100] p-0 sm:p-6 overflow-y-auto">
          <div className="bg-white shadow-2xl w-full h-full sm:h-auto sm:max-w-5xl sm:rounded-2xl flex flex-col sm:max-h-[90vh] overflow-hidden animate-fade-in-up">
            {/* Header */}
            <div className="flex items-center justify-between p-5 sm:p-6 border-b border-slate-100 shrink-0 bg-white sticky top-0 z-10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-slate-800">
                  {editMode ? "Actualizar Ingreso" : "Nuevo Ingreso"}
                </h3>
              </div>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="text-slate-400 hover:text-slate-600 transition-colors bg-slate-50 hover:bg-slate-100 rounded-full p-2"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Contenido principal */}
            <div className="flex-grow overflow-y-auto custom-scrollbar bg-slate-50/30">
              <div className="flex flex-col md:flex-row min-h-full">
                {/* Sección izquierda: Detalles ingreso */}
                <div className="md:w-5/12 p-5 sm:p-6 border-b md:border-b-0 md:border-r border-slate-100">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-5">
                    <div className="col-span-2">
                      <label className="block mb-2 text-sm font-semibold text-slate-700">Proveedor</label>
                      <ReactSelect
                        options={proveedores}
                        value={proveedorSeleccionado}
                        onChange={setProveedorSeleccionado}
                        placeholder="Seleccione el proveedor"
                        classNamePrefix="react-select"
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: base => ({ ...base, zIndex: 9999 }),
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor: state.isFocused ? '#6366f1' : '#cbd5e1',
                            borderRadius: '0.8rem',
                            minHeight: '44px',
                            padding: '2px',
                            boxShadow: state.isFocused ? '0 0 0 2px rgba(99, 102, 241, 0.2)' : 'none',
                          })
                        }}
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block mb-2 text-sm font-semibold text-slate-700">Tipo de factura</label>
                      <ReactSelect
                        options={opcionesTipoDocumento}
                        value={opcionesTipoDocumento.find((opt) => opt.value === String(form.tipoDocumento)) || null}
                        onChange={(val) => handleSelectChange(val, "tipoDocumento")}
                        placeholder="Tipo de documento"
                        isDisabled={!!rawJsonString}
                        isSearchable
                        isClearable={!rawJsonString}
                        classNamePrefix="react-select"
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: base => ({ ...base, zIndex: 9999 }),
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor: state.isFocused ? '#6366f1' : '#cbd5e1',
                            borderRadius: '0.8rem',
                            minHeight: '44px',
                            padding: '2px',
                            boxShadow: state.isFocused ? '0 0 0 2px rgba(99, 102, 241, 0.2)' : 'none',
                          })
                        }}
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block mb-2 text-sm font-semibold text-slate-700">Fecha Ingreso</label>
                      <input
                        type="date"
                        value={fechaIngreso}
                        onChange={(e) => setFechaIngreso(e.target.value)}
                        className="w-full border border-slate-300 rounded-xl px-4 py-2.5 focus:ring-4 focus:ring-indigo-100 focus:border-indigo-500 transition-all text-sm h-[44px]"
                      />
                    </div>
                    <div className="col-span-1">
                      <label className="block mb-2 text-sm font-semibold text-slate-700">Cód. Gen.</label>
                      <input
                        type="text"
                        value={codigoFactura}
                        onChange={(e) => setCodigoFactura(e.target.value)}
                        placeholder="Mandatorio"
                        className="w-full border border-slate-300 rounded-xl px-4 py-2.5 focus:ring-4 focus:ring-indigo-100 focus:border-indigo-500 transition-all text-sm h-[44px]"
                      />
                    </div>
                    <div className="col-span-1">
                      <label className="block mb-2 text-sm font-semibold text-slate-700">No. Control</label>
                      <input
                        type="text"
                        value={numeroFactura}
                        onChange={(e) => setNumeroFactura(e.target.value)}
                        placeholder="Mandatorio"
                        className="w-full border border-slate-300 rounded-xl px-4 py-2.5 focus:ring-4 focus:ring-indigo-100 focus:border-indigo-500 transition-all text-sm h-[44px]"
                      />
                    </div>
                  </div>
                </div>

                {/* Sección derecha: Insumos */}
                <div className="md:w-7/12 p-5 sm:p-6 flex flex-col flex-grow">
                  <div className="flex justify-between items-center mb-6">
                    <h4 className="font-bold text-slate-800">Detalle de Ingreso</h4>
                    <button
                      className="bg-indigo-600 text-white font-medium px-4 py-2 rounded-xl hover:bg-indigo-700 transition-colors text-sm flex items-center gap-2 shadow-lg shadow-indigo-100"
                      onClick={() => setShowInsumoModal(true)}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Agregar Insumo
                    </button>
                  </div>

                  <div className="space-y-4">
                    {insumosSeleccionados.length > 0 ? (
                      <div className="space-y-3">
                        {insumosSeleccionados.map((prod, i) => (
                          <div key={i} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm group relative">
                            <button
                              onClick={() => {
                                const updated = [...insumosSeleccionados];
                                updated.splice(i, 1);
                                setInsumosSeleccionados(updated);
                              }}
                              className="absolute -top-2 -right-2 w-6 h-6 bg-rose-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                            >
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                            <div className="flex flex-col gap-3">
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                <span className={`font-bold text-sm leading-tight ${prod.isPlaceholder ? "text-amber-600" : "text-slate-800"}`}>
                                  {prod.label}
                                </span>
                                <span className="text-[10px] font-bold text-indigo-500 uppercase bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100 w-fit">
                                  Unidad: {prod.unidad}
                                </span>
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="text-[10px] uppercase font-bold text-slate-400 mb-1 block">Cantidad</label>
                                  <input
                                    type="number"
                                    value={prod.cantidad}
                                    onChange={(e) => handleChangeCantidad(i, e.target.value)}
                                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 transition-all font-medium"
                                  />
                                </div>
                                <div>
                                  <label className="text-[10px] uppercase font-bold text-slate-400 mb-1 block">Precio Costo</label>
                                  <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                                    <input
                                      type="number"
                                      value={prod.precio}
                                      onChange={(e) => handleChangePrecio(i, e.target.value)}
                                      className="w-full pl-7 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 transition-all font-medium"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="py-12 flex flex-col items-center justify-center p-8 text-center bg-white rounded-2xl border border-dashed border-slate-300">
                        <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4 text-slate-400">
                          <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                          </svg>
                        </div>
                        <p className="text-slate-500 font-bold">Sin insumos</p>
                        <p className="text-xs text-slate-400 mt-1">Haga clic en 'Agregar Insumo' para comenzar.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer Móvil */}
              <div className="md:hidden p-5 border-t border-slate-200 bg-white flex flex-col gap-3 mt-auto">
                <div className="flex justify-between items-center bg-indigo-50 p-4 rounded-xl border border-indigo-100 mb-2">
                  <span className="text-indigo-600 font-black uppercase text-[10px] tracking-widest">Total Final</span>
                  <div className="flex flex-col items-end">
                    <span className="text-indigo-700 font-black text-xl">
                      ${insumosSeleccionados.reduce((acc, p) => acc + (Number(p.cantidad) * Number(p.precio)), 0).toFixed(2)}
                    </span>
                  </div>
                </div>
                <button
                  disabled={submitting || insumosSeleccionados.length === 0}
                  onClick={handleProcesarIngreso}
                  className={`w-full py-3.5 rounded-xl flex items-center justify-center gap-3 font-bold text-white text-sm uppercase tracking-wide ${submitting || insumosSeleccionados.length === 0 ? "bg-indigo-300" : "bg-indigo-600"}`}
                >
                  Guardar Ingreso
                </button>
              </div>
            </div>

            {/* Footer Desktop (IDÉNTICO A INGRESOS.JSX) */}
            <div className="hidden md:flex p-4 sm:p-6 border-t border-slate-200 bg-white items-center justify-between shrink-0">
              <div className="flex items-center gap-4 bg-indigo-50 px-6 py-3 rounded-2xl border border-indigo-100 shadow-sm">
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase font-black text-indigo-400 tracking-widest leading-none mb-1">
                    TOTAL A PAGAR
                  </span>
                  <div className="flex items-baseline gap-2">
                    <span className="text-indigo-600 font-extrabold text-2xl leading-none">
                      ${insumosSeleccionados.reduce((acc, p) => acc + (Number(p.cantidad) * Number(p.precio)), 0).toFixed(2)}
                    </span>
                  </div>
                </div>
                <div className="h-8 w-px bg-indigo-200/50 mx-2"></div>
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase font-bold text-slate-400 leading-none mb-1">INSUMOS</span>
                  <span className="text-slate-700 font-bold text-lg leading-none">{insumosSeleccionados.length}</span>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="px-8 py-3 rounded-xl border border-slate-300 text-slate-700 font-bold hover:bg-slate-50 transition-all text-sm uppercase tracking-wide"
                >
                  CANCELAR
                </button>
                <button
                  disabled={submitting || insumosSeleccionados.length === 0}
                  onClick={handleProcesarIngreso}
                  className={`px-10 py-3 rounded-xl flex items-center justify-center gap-3 font-bold shadow-lg shadow-indigo-100 transition-all text-white text-sm uppercase tracking-wide ${submitting || insumosSeleccionados.length === 0
                    ? "bg-indigo-400 cursor-not-allowed"
                    : "bg-indigo-600 hover:bg-indigo-700 hover:-translate-y-0.5"
                    }`}
                >
                  {submitting && <span className="w-5 h-5 border-3 border-white/30 border-t-white rounded-full animate-spin"></span>}
                  GUARDAR INGRESO
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Selección Insumos - Rediseño Premium */}
      {showInsumoModal && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex justify-center items-center z-[120] p-4 sm:p-6">
          <div className="bg-white shadow-2xl w-full h-full sm:h-auto sm:max-w-4xl sm:rounded-2xl flex flex-col sm:max-h-[90vh] overflow-hidden animate-fade-in-up">
            {/* Header */}
            <div className="flex items-center justify-between p-5 sm:p-6 border-b border-slate-100 shrink-0 bg-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600 font-bold">
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
                <h3 className="text-xl font-black text-slate-800 tracking-tight">Seleccionar Insumos</h3>
              </div>
              <button
                onClick={() => setShowInsumoModal(false)}
                className="text-slate-400 hover:text-slate-600 transition-colors bg-slate-50 hover:bg-slate-100 rounded-full p-2"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Contenido Principal */}
            <div className="p-5 sm:p-6 flex flex-col gap-5 overflow-hidden">
              {/* Buscador con lupa */}
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors group-focus-within:text-indigo-500">
                  <svg className="h-5 w-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Buscar insumo por SKU, nombre..."
                  value={buscarInsumo}
                  onChange={(e) => {
                    setBuscarInsumo(e.target.value);
                    setPageIns(1);
                  }}
                  className="pl-11 pr-4 py-3.5 w-full border border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-100 focus:border-indigo-500 transition-all bg-slate-50 focus:bg-white text-sm font-bold text-slate-700"
                />
              </div>

              {/* Contenedor de Tabla */}
              <div className="flex-grow overflow-auto rounded-xl border border-slate-100 shadow-sm custom-scrollbar">
                <table className="w-full text-left text-sm border-collapse">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-white text-slate-400 font-black text-[10px] uppercase tracking-widest border-b border-slate-100">
                      <th className="px-6 py-4">SKU</th>
                      <th className="px-6 py-4">NOMBRE</th>
                      <th className="px-6 py-4">PRECIO / U.</th>
                      <th className="px-6 py-4 text-center">EXISTENCIA</th>
                      <th className="px-6 py-4 text-center">ACCIÓN</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50 bg-white">
                    {insumos.length > 0 ? (
                      insumos.map((item) => {
                        const isAdded = insumosSeleccionados.some(p => p.value === item.id);
                        const stock = item.CocinaInsumoExistencia?.[0]?.stock || 0;
                        
                        return (
                          <tr key={item.id} className="hover:bg-slate-50/50 transition-colors group">
                            <td className="px-6 py-4 font-black text-slate-800 tracking-tight">{item.sku}</td>
                            <td className="px-6 py-4 text-slate-600 font-semibold">{item.name}</td>
                            <td className="px-6 py-4 text-emerald-600 font-bold tabular-nums">
                              ${Number(item.price || 0).toFixed(5)}
                            </td>
                            <td className="px-6 py-4 text-center">
                              <span className={`px-3 py-1 rounded-lg text-[11px] font-black border ${stock <= 5 ? 'bg-rose-50 text-rose-500 border-rose-100' : 'bg-emerald-50 text-emerald-600 border-emerald-100'}`}>
                                {stock}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-center">
                              {isAdded ? (
                                <div className="flex items-center justify-center px-4 py-2 bg-emerald-50 text-emerald-600 rounded-xl text-xs font-black border border-emerald-100 gap-1.5 min-w-[120px]">
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                  </svg>
                                  Agregado
                                </div>
                              ) : (
                                <button
                                  onClick={() => handleAddInsumo(item)}
                                  className="w-full flex items-center justify-center px-4 py-2 bg-indigo-50 text-indigo-600 rounded-xl text-xs font-black border border-indigo-100 hover:bg-indigo-600 hover:text-white transition-all gap-1.5 active:scale-95 min-w-[120px] shadow-sm hover:shadow-indigo-200"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                                  </svg>
                                  + Agregar
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="5" className="px-6 py-20 text-center">
                           <div className="flex flex-col items-center gap-2 text-slate-400">
                              <svg className="w-12 h-12 opacity-20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.172 9.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <p className="font-bold">No se encontraron resultados</p>
                           </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Paginación - Diseño Screenshot */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2">
                <span className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em]">
                  Página {pageIns} de {totalPagesIns}
                </span>
                <div className="flex items-center gap-1.5">
                  <button
                    disabled={pageIns === 1}
                    onClick={() => setPageIns(p => p - 1)}
                    className="px-4 py-2.5 rounded-xl border border-slate-200 text-xs font-black text-slate-500 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all active:scale-95"
                  >
                    Anterior
                  </button>
                  <div className="flex gap-1.5">
                    {[...Array(totalPagesIns)].map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setPageIns(idx + 1)}
                        className={`w-9 h-9 rounded-xl text-xs font-black transition-all ${pageIns === idx + 1 ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200' : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'}`}
                      >
                        {idx + 1}
                      </button>
                    ))}
                  </div>
                  <button
                    disabled={pageIns === totalPagesIns}
                    onClick={() => setPageIns(p => p + 1)}
                    className="px-4 py-2.5 rounded-xl border border-slate-200 text-xs font-black text-slate-500 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all active:scale-95"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-5 sm:p-6 border-t border-slate-100 bg-white shrink-0 flex justify-end">
              <button
                onClick={() => setShowInsumoModal(false)}
                className="w-full sm:w-auto px-10 py-3.5 bg-indigo-600 text-white rounded-xl font-black hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-100 active:scale-[0.98] text-sm uppercase tracking-widest"
              >
                Cerrar y Volver
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Detalle (Réplica de Ingresos.jsx) */}
      {showDetailModal && selectedIngreso && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex justify-center items-center z-[110] p-4">
           <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]">
              <div className="p-6 bg-indigo-600 text-white rounded-t-2xl flex justify-between items-center shrink-0">
                 <h3 className="text-lg font-black italic tracking-tighter uppercase tracking-widest">Detalle de Ingreso (Insumos)</h3>
                 <button onClick={() => setShowDetailModal(false)} className="text-white text-2xl font-bold">×</button>
              </div>
              <div className="p-6 overflow-y-auto bg-white">
                 <div className="grid grid-cols-2 gap-4 mb-6 border-b pb-4">
                    <div><span className="text-[10px] font-black text-slate-400 uppercase">Proveedor</span><p className="font-bold text-slate-800">{selectedIngreso.Proveedore?.name}</p></div>
                    <div><span className="text-[10px] font-black text-slate-400 uppercase">Factura</span><p className="font-mono text-sm">{selectedIngreso.numero_factura || "---"}</p></div>
                 </div>
                 <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                       <tr><th className="px-4 py-2 text-left">Insumo</th><th className="px-4 py-2 text-center">Cant.</th><th className="px-4 py-2 text-right">Subtotal</th></tr>
                    </thead>
                    <tbody className="divide-y">
                       {selectedIngreso.IngresosDetalles?.map((det) => (
                          <tr key={det.id}>
                             <td className="px-4 py-3"><p className="font-bold text-slate-700">{det.CocinaInsumo?.name || det.Producto?.name}</p><p className="text-[10px] text-indigo-500 font-bold uppercase">{det.CocinaInsumo?.UnidadMedida?.name || det.Producto?.UnidadMedida?.name || "---"}</p></td>
                             <td className="px-4 py-3 text-center"><span className="bg-slate-100 px-3 py-1 rounded-lg font-bold">{det.cantidad}</span></td>
                             <td className="px-4 py-3 text-right font-black text-slate-800">${(det.cantidad * Number(det.precio || 0)).toFixed(2)}</td>
                          </tr>
                       ))}
                    </tbody>
                 </table>
              </div>
              <div className="p-6 border-t bg-slate-50 rounded-b-2xl flex items-center justify-between">
                 <div><span className="text-[10px] uppercase font-black text-slate-400">TOTAL FINAL</span><p className="text-2xl font-black text-indigo-600 tabular-nums">${Number(selectedIngreso.total || 0).toFixed(2)}</p></div>
                 <button onClick={() => setShowDetailModal(false)} className="bg-indigo-600 text-white px-8 py-2 rounded-xl font-bold uppercase tracking-widest">Cerrar</button>
              </div>
           </div>
        </div>
      )}
    </div>
  );
};

export default IngresosInsumos;
